import { Environment, getCurrentEnvironment, Region } from './environment';

export type DataCenterOption = { value: string; flag: string; name: string; description: string };

/**
 * The data centers an account can be created in. Picked once at signup (or a
 * Vercel install) and never changed, hence the warning beside the cards.
 */
export const DATA_CENTERS: DataCenterOption[] = [
  { value: 'us', flag: '🇺🇸', name: 'United States', description: 'For customers mostly outside Europe' },
  { value: 'eu', flag: '🇪🇺', name: 'Europe', description: 'For customers in the EU or UK' },
];

const DATA_CENTER_FLAGS: Record<string, string> = {
  us: '🇺🇸',
  eu: '🇪🇺',
};

/**
 * Formats an account's data center for display as a compact region badge, e.g.
 * "🇺🇸 US". Returns an empty string for an empty/unknown-to-us value so callers
 * can skip rendering; an unknown non-empty code falls back to the upper-cased
 * code without a flag.
 */
export function formatDataCenterShort(dataCenter: string | null | undefined): string {
  if (dataCenter == null || dataCenter === '') {
    return '';
  }
  const flag = DATA_CENTER_FLAGS[dataCenter] ?? '';
  return `${flag} ${dataCenter.toUpperCase()}`.trim();
}

/**
 * Adds the account's region to a documentation URL. The docs site reads
 * `region=eu` and shows the EU hosts and examples; without it an EU account
 * gets the US ones.
 */
export function docsUrlWithRegion(url: string, region: Region): string {
  const parsed = new URL(url);
  if (region === Region.EU) {
    parsed.searchParams.append('region', Region.EU);
  }
  return parsed.toString();
}

/**
 * The host segment of the tracking script the JavaScript snippet loads —
 * `https://assets.customer.io/assets/<segment>.js`.
 */
export function trackAssetHost(region: Region): string {
  return region === Region.EU ? 'track-eu' : 'track';
}

/** The parts of `window.location` a development host resolver reads. */
export interface TrackApiLocation {
  protocol: string;
  host: string;
  hostname: string;
}

/**
 * Where a local stack serves the track API.
 *
 * A devbox names its hosts `fly-<name>` or `fly.<domain>` and answers on the
 * matching `track-` host; anything else — a tunnelled devbox, plain localhost
 * — answers on port 9100. Only knowable from the live location, which is why
 * every development host in this module resolves per call rather than from a
 * table.
 */
export function developmentTrackApiHost(location: TrackApiLocation): string {
  if (location.host.startsWith('fly-')) {
    return `${location.protocol}//${location.host.replace('fly-', 'track-')}`;
  }
  if (location.host.startsWith('fly.')) {
    return `${location.protocol}//${location.host.replace('fly.', 'track.')}`;
  }
  return `${location.protocol}//${location.hostname}:${LOCAL_TRACK_PORT}`;
}

/** The port the track API listens on in a local checkout. */
const LOCAL_TRACK_PORT = 9100;

/**
 * The host a webhook trigger's endpoint is served from, for one data center.
 *
 * Production splits by data center; standalone serves both from one staging
 * host; development derives the host from where the app is being served,
 * because a devbox's track host is its own host with the `fly-` prefix swapped
 * for `track-`, and a local checkout reaches it on a fixed port.
 *
 * `location` is a parameter rather than a read of `window` so every branch is
 * testable without standing up a location, and `environment` defaults to the
 * current one so callers state only what they know.
 */
export function appApiHostForDataCenter(
  dataCenter: string,
  location: TrackApiLocation = window.location,
  environment: Environment = getCurrentEnvironment(),
): string {
  switch (environment) {
    case Environment.PRODUCTION:
      return dataCenter === Region.EU ? 'https://api-eu.customer.io' : 'https://api.customer.io';
    case Environment.STANDALONE:
      return 'https://track-gce-staging.customer.io';
    case Environment.DEVELOPMENT:
      return developmentTrackApiHost(location);
    default: {
      const _exhaustive: never = environment;
      return String(_exhaustive);
    }
  }
}

/**
 * Where a webhook trigger listens, or an empty string before its endpoint has
 * been minted.
 *
 * An empty string rather than null because the URL is shown and copied as
 * text: a campaign whose endpoint is still being created has nothing to show,
 * which is the same nothing the spinner stands in front of.
 */
export function webhookUrl(webhookId: string | null | undefined, host: string): string {
  if (webhookId == null || webhookId === '') {
    return '';
  }
  return `${host}/v1/webhook/${webhookId}`;
}
